'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import RootLayouts from '@/layouts/Rootlayouts';
import { formatParams } from '@/utils/removeSpace';
import { generateFAQSchema, generateWebPageSchema } from '@/utils/seo/schema';
import generateClgListSEOData from '@/utils/seo/seoClgListUtils';

import ArticleComponent from '@/components/colleges/listing/ArticleComponent';
// import CollegeListingPageFilter from '@/components/colleges/listing/CollegeListingPageFilter';
import CollegeListItem from '@/components/colleges/listing/CollegeListItem';
import Breadcrumb from '@/components/common/seo/BreadCrumbs';
import styles from '@/styles/featuretag.module.css';

const CollegeListSection = dynamic(() => import('@/components/colleges/listing/CollegeListSection'), {
  ssr: false,
});

const CollegeListingPageFilter = dynamic(() => import('@/components/colleges/listing/CollegeListingPageFilter'), {
  ssr: false,
  loading: () => <div className="h-16 w-full bg-gray-100" />,
});

const Banner = dynamic(() => import('@/components/colleges/listing/BannerComponent'), {
  ssr: false,
  // loading: () => <div className="h-20 w-full animate-pulse rounded-md bg-gray-100" />,
});

const CollegeListingComponent = ({
  colleges,
  collegesParam,
  slugInfo,
  filters,
  featuredColleges,
  bannerData,
  articleData,
  geoLocation,
  filterData,
  filterSecStreamData,
}: any) => {
  const seoData = generateClgListSEOData(slugInfo, collegesParam, articleData?.seo);

  const { page_title_content } = seoData;

  const isAbroad = slugInfo?.country_name!=='India';

  const onlineCollegeTagIds = new Set([2012]);
  const isOnlineColleges = collegesParam.includes('online-colleges-in');
  const featuredCollegeIds = new Set(featuredColleges?.map((college: any) => college.id));

  let finalCollegeList;
  if (isOnlineColleges) {
    // For online colleges, put those with IDs in onlineCollegeTagIds at the top
    const featuredOnlineColleges = colleges?.results?.filter((college: any) => onlineCollegeTagIds.has(college.id));
    const otherColleges = colleges?.results?.filter((college: any) => !onlineCollegeTagIds.has(college.id));
    finalCollegeList = [...featuredOnlineColleges, ...otherColleges];
  } else {
    // For other cases, use API-provided featuredColleges
    const filteredColleges = colleges?.results?.filter((college: any) => !featuredCollegeIds?.has(college?.id));
    finalCollegeList = [...featuredColleges, ...filteredColleges];
  }

  const domainUrl = process.env.NEXT_PUBLIC_DOMAIN_URL;
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: slugInfo?.country_name === 'India' ? `${domainUrl}` : `${domainUrl}/study-abroad`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: `${formatParams(collegesParam)}`,
      item: `${domainUrl}/${collegesParam}`,
    },
  ];

  // Create ItemList schema for colleges
  const collegeListSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: articleData?.heading ?? page_title_content,
    description:
      articleData?.description ??
      `Check here the list of best ${
        articleData?.heading ?? page_title_content
      } along with their fees structure, admission process, courses, reviews, placements, ranking and eligibility criteria details at CollegeHai.`,
    numberOfItems: colleges?.pagination?.total ?? 0,
    url: `${domainUrl}/${collegesParam}`,
    itemListElement: finalCollegeList?.slice(0, 30).map((college: any, index: number) => {
      const isOnlineCollege = college?.is_online_college;

      const collegeUrl = isOnlineCollege
        ? `/colleges/${college?.college_url}`
        : `/colleges/${college?.college_url}/college-info`;

      return {
        '@type': 'ListItem',
        position: index + 1,
        url: `${domainUrl}${collegeUrl}`,
      };
    }),
  };

  const title = `${formatParams(collegesParam)} Colleges in India 2025`;

  const pageUrl = `${domainUrl}/${collegesParam}`;

  const webPageSchema = generateWebPageSchema(title, pageUrl);

  const faqContent = articleData?.page_data?.filter((item: any) => item.__component === 'article.faq') || [];

  const faqData = faqContent.flatMap((faqBlock: any) => faqBlock.FAQ) || [];

  const faqSchema = generateFAQSchema(faqData?.slice(0, 10));

  return (
    <>
      {/* Webpage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(collegeListSchema),
        }}
      />

      {faqSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      )}
      <RootLayouts isAbroad={isAbroad}>
        <section className="bg-gray-50">
          <section className="mt-20 lg:pt-8">
            <div className="mx-auto w-[95%] md:w-[90%]">
              <Breadcrumb color="text-primary" items={breadcrumbItems} />
            </div>
            <h1 className="mx-auto w-[95%] text-xl font-bold leading-tight text-primary md:w-[90%] md:text-2xl">
              {articleData?.heading ?? page_title_content}
            </h1>

            {/* {page_content_description ? (
              <p className="w-[95%] md:w-[90%] mx-auto text-sm md:text-base font-normal leading-relaxed text-gray-800 mt-2">
                {formatDescriptionToJSX(page_content_description)}
              </p>
            ) : null} */}
            {/* Article Overview component */}
            {articleData && articleData?.page_data?.length > 0 && <ArticleComponent articleData={articleData} />}
            <div className="hidden lg:block"> {bannerData?.results && <Banner banners={bannerData?.results} />} </div>
          </section>
          {/* Filter component */}
          <section>
            <div className="relative flex-col gap-y-4 font-inter lg:flex lg:py-2">
              {!isOnlineColleges && (
                <div className="sticky top-14 !z-50 hidden items-center rounded border bg-white px-4 py-2 shadow-lg md:mx-10 lg:top-[85px] lg:mx-auto lg:flex lg:w-[90%] lg:py-3 ">
                  <CollegeListingPageFilter
                    filters={filters}
                    geoLocation={geoLocation}
                    filterData={filterData}
                    filterSecStreamData={filterSecStreamData}
                  />
                </div>
              )}

              {/* COllege List section */}
              <div className="z-10 w-full flex-1 md:mt-2">
                <div className="mx-auto w-[95%] flex-1 md:w-[90%]">
                  <div className="my-2 flex items-center justify-between gap-2 md:mt-9 lg:mt-[5px] lg:items-start">
                    <div className="px-2 text-base text-primary-text">
                      Showing <span className="font-semibold">{colleges?.pagination?.total} </span> Colleges
                    </div>
                  </div>

                  <div className="w-full">
                    {finalCollegeList?.map((college: any, index: number) => {
                      const isIndianCollege = collegesParam.includes('-india');
                      // Use hardcoded IDs for featured on online page, else use API
                      const isFeatured = isOnlineColleges
                        ? onlineCollegeTagIds.has(college?.id)
                        : featuredCollegeIds?.has(college?.id);
                      const shouldShowBanner = index === 3;
                      return (
                        <div className="relative w-full" key={college?.id}>
                          <CollegeListItem
                            college={college}
                            id={college?.college_url}
                            className="w-full"
                            isAbroadCollege={!isIndianCollege}
                          />

                          {/* Featured Tag */}
                          {isFeatured && (
                            <div className={`${styles.featuredTag} bg-green-600`}>
                              <span className="text-[0.7rem] text-white">Featured</span>
                            </div>
                          )}
                          {shouldShowBanner && bannerData?.results && (
                            <div className="lg:hidden">
                              {' '}
                              <Banner banners={bannerData?.results} />{' '}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <CollegeListSection
                    collegesParam={collegesParam}
                    filters={filters}
                    featuredColleges={featuredCollegeIds}
                  />
                </div>
              </div>
            </div>
          </section>
        </section>
      </RootLayouts>
    </>
  );
};

export default CollegeListingComponent;
